"use client";

import { c as _c } from "react/compiler-runtime";
import styles from "./SubscriptionsReport.module.css";
import { useAuth } from "@/components/auth/Auth";
import { useCookieConsent } from "@/components/cookies/CookieConsentContext";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import { publicEnv } from "@/config/env";
import { shouldHideSubscriptions } from "@/components/user/layout/userPageVisibility";
import type { MemeSeason } from "@/entities/ISeason";
import type { SeasonMintRow } from "@/components/meme-calendar/meme-calendar.helpers";
import { displayedSeasonNumberFromIndex, formatFullDate, getCardsRemainingUntilEndOf, getUpcomingMintsAcrossSeasons, isMintingToday } from "@/components/meme-calendar/meme-calendar.helpers";
import type { Paginated } from "@/components/pagination/Pagination";
import Pagination from "@/components/pagination/Pagination";
import ShowMoreButton from "@/components/show-more-button/ShowMoreButton";
import type { RedeemedSubscriptionCounts } from "@/generated/models/RedeemedSubscriptionCounts";
import type { SubscriptionCounts } from "@/generated/models/SubscriptionCounts";
import { Time } from "@/helpers/time";
import useCapacitor from "@/hooks/useCapacitor";
import { commonApiFetch } from "@/services/api/common-api";
import { getAuthJwt, getStagingAuth } from "@/services/auth/auth.utils";
import { sanitizeErrorForUser } from "@/utils/error-sanitizer";
import Image from "next/image";
import Link from "next/link";
import useDownloader from "react-use-downloader";
import { useEffect, useMemo, useRef, useState } from "react";
import { Col, Container, Form, Row } from "react-bootstrap";
const PAGE_SIZE = 10;
export default function SubscriptionsReportComponent() {
  const capacitor = useCapacitor();
  const {
    country
  } = useCookieConsent();
  const {
    connectedProfile,
    setToast
  } = useAuth();
  const hideSubscriptions = shouldHideSubscriptions({
    capacitorIsIos: capacitor.isIos,
    country
  });
  const pastDropsTarget = useRef<HTMLDivElement>(null);
  const upcomingToggleRef = useRef<HTMLDivElement>(null);
  const upcomingTableTopRef = useRef<HTMLDivElement>(null);
  const [upcomingLoading, setUpcomingLoading] = useState(true);
  const [upcomingCounts, setUpcomingCounts] = useState<SubscriptionCounts[]>([]);
  const [redeemedLoading, setRedeemedLoading] = useState(true);
  const [redeemedCounts, setRedeemedCounts] = useState<RedeemedSubscriptionCounts[]>([]);
  const [totalRedeemed, setTotalRedeemed] = useState(0);
  const [redeemedPage, setRedeemedPage] = useState<number>(1);
  const UPCOMING_PAGE_SIZE = 5;
  const [upcomingVisible, setUpcomingVisible] = useState(UPCOMING_PAGE_SIZE);
  const [animateFromIndex, setAnimateFromIndex] = useState<number | null>(null);
  const firstNewRowRef = useRef<HTMLTableRowElement>(null);
  const [availableSeasons, setAvailableSeasons] = useState<MemeSeason[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<string>("");
  const [seasonOptionsLoaded, setSeasonOptionsLoaded] = useState(false);

  // Scroll first new row into view after show-more renders
  useEffect(() => {
    if (animateFromIndex === null) return;
    requestAnimationFrame(() => {
      firstNewRowRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    });
  }, [animateFromIndex, upcomingVisible]);
  const [now] = useState(new Date());
  const rows = useMemo<SeasonMintRow[]>(() => getUpcomingMintsAcrossSeasons(upcomingCounts.length || 50, now), [now, upcomingCounts.length]);
  async function fetchUpcomingCounts(count: number) {
    return await commonApiFetch<SubscriptionCounts[]>({
      endpoint: `subscriptions/upcoming-memes-counts?card_count=${count}`
    });
  }
  async function fetchRedeemedCounts(page: number) {
    return await commonApiFetch<Paginated<RedeemedSubscriptionCounts>>({
      endpoint: `subscriptions/redeemed-memes-counts`,
      params: {
        page_size: PAGE_SIZE.toString(),
        page: page.toString()
      }
    });
  }
  async function fetchSeasons() {
    return await commonApiFetch<MemeSeason[]>({
      endpoint: "new_memes_seasons"
    });
  }
  useEffect(() => {
    const fetchData = async () => {
      try {
        let remainingCountForSeason = getCardsRemainingUntilEndOf("szn");
        const redeemed = await fetchRedeemedCounts(1);
        if (isMintingToday()) {
          const latestDrop = redeemed.data[0];
          if (latestDrop?.mint_date) {
            const mintDate = Time.fromString(latestDrop.mint_date);
            if (mintDate.toIsoDateString() !== Time.now().toIsoDateString()) {
              remainingCountForSeason += 1;
            }
          }
        }
        const upcoming = await fetchUpcomingCounts(remainingCountForSeason);
        setRedeemedCounts(redeemed.data);
        setTotalRedeemed(redeemed.count);
        setUpcomingCounts(upcoming);
      } finally {
        setRedeemedLoading(false);
        setUpcomingLoading(false);
      }
    };
    fetchData();
  }, []);
  useEffect(() => {
    const fetchSeasonOptions = async () => {
      try {
        const seasons = await fetchSeasons();
        setAvailableSeasons(seasons);
      } catch (error) {
        console.error("Failed to fetch seasons for subscriptions report:", error);
      } finally {
        setSeasonOptionsLoaded(true);
      }
    };
    fetchSeasonOptions();
  }, []);
  useEffect(() => {
    if (redeemedLoading) return;
    const fetchData_0 = async () => {
      setRedeemedLoading(true);
      try {
        const redeemed = await fetchRedeemedCounts(redeemedPage);
        setRedeemedCounts(redeemed.data);
        setTotalRedeemed(redeemed.count);
      } finally {
        setRedeemedLoading(false);
      }
    };
    fetchData_0();
  }, [redeemedPage]);
  function renderEmptyState(loading: boolean, type: string) {
    if (loading) {
      return <span className="tw-animate-pulse tw-text-sm tw-text-gray-400">
          Loading {type} drops...
        </span>;
    }
    return <>No Subscriptions Found</>;
  }
  const selectedSeason = availableSeasons.find(season => season.id.toString() === selectedSeasonId) ?? null;
  const buildDownloadHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {};
    const apiAuth = getStagingAuth();
    const allowlistAuth = getAuthJwt();
    if (apiAuth) {
      headers["x-6529-auth"] = apiAuth;
    }
    if (allowlistAuth) {
      headers["Authorization"] = `Bearer ${allowlistAuth}`;
    }
    return headers;
  };
  const {
    download,
    error: csvDownloadError,
    isInProgress: isDownloadingCsv
  } = useDownloader({
    headers: buildDownloadHeaders()
  });
  const csvDownloadUrl = useMemo(() => {
    const downloadUrl = new URL("/api/subscriptions/redeemed-memes-counts/download", publicEnv.API_ENDPOINT);
    if (selectedSeason) {
      downloadUrl.searchParams.set("szn", selectedSeason.id.toString());
    }
    return downloadUrl.toString();
  }, [selectedSeason]);
  useEffect(() => {
    if (!csvDownloadError?.errorMessage) {
      return;
    }

    // react-use-downloader prefixes messages with "{status} - {statusText}: "
    // e.g. "400 - : No subscription data available" — strip that prefix
    const rawMessage = csvDownloadError.errorMessage;
    const cleanMessage = rawMessage.replace(/^\d{3}\s*-[^:]*:\s*/, "").trim();
    setToast({
      message: sanitizeErrorForUser(cleanMessage || rawMessage),
      type: "error"
    });
  }, [csvDownloadError, setToast]);
  const onDownloadCsv = async () => {
    if (isDownloadingCsv) {
      return;
    }
    await download(csvDownloadUrl, getRedeemedCsvFilename(selectedSeason));
  };
  const shouldShowDownloadSection = !upcomingLoading && !redeemedLoading && seasonOptionsLoaded;
  return <Container className="tw-mx-auto tw-px-2 tw-py-5 lg:tw-px-6 xl:tw-px-8">
      <Row>
        <Col className="d-flex flex-wrap align-items-center justify-content-between">
          <h1>Subscriptions Report</h1>
          <div className="tw-flex tw-items-center tw-gap-3">
            {connectedProfile && !hideSubscriptions && <Link href={`/${connectedProfile.normalised_handle}/subscriptions`} className="decoration-none" aria-label="Learn more about The Memes subscriptions">
                <button className="tw-rounded-lg tw-border-0 tw-bg-primary-500 tw-p-2 tw-ring-1 tw-ring-inset tw-transition tw-duration-300 tw-ease-out hover:tw-bg-primary-600">
                  My Subscriptions
                </button>
              </Link>}
            <Link href="/about/subscriptions" className="decoration-hover-underline" aria-label="Learn more about The Memes subscriptions">
              Learn More
            </Link>
          </div>
        </Col>
      </Row>
      <div ref={upcomingTableTopRef} />
      <Row className="pt-3">
        <Col className="tw-flex tw-items-center tw-gap-3">
          <span className="font-larger font-bolder decoration-none">
            Upcoming Drops
          </span>
          {upcomingLoading && <CircleLoader size={CircleLoaderSize.MEDIUM} />}
        </Col>
      </Row>
      <Row className="pt-3">
        <Col>
          {upcomingCounts?.length > 0 ? <>
              <table className="tw-w-full tw-border-separate tw-border-spacing-0 tw-overflow-hidden tw-rounded-xl tw-border tw-border-iron-700 tw-bg-iron-900">
                <caption className="tw-sr-only">
                  Table listing upcoming meme card subscriptions
                </caption>
                <thead>
                  <tr className="tw-bg-iron-900 tw-text-left tw-text-sm tw-uppercase tw-tracking-wider tw-text-gray-300">
                    <th className="tw-w-3/4 tw-border-b tw-border-iron-700 tw-px-6 tw-py-3 tw-font-semibold">
                      Meme Card
                    </th>
                    <th className="tw-w-1/4 tw-border-b tw-border-iron-700 tw-px-6 tw-py-3 tw-text-center tw-font-semibold">
                      Subscriptions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingCounts.slice(0, upcomingVisible).map((count_0, index) => {
                const isNew = animateFromIndex !== null && index >= animateFromIndex;
                return <tr key={count_0.token_id} ref={index === animateFromIndex ? firstNewRowRef : null} className={[index % 2 === 0 ? "tw-bg-iron-800 hover:tw-bg-iron-700" : "tw-bg-iron-900 hover:tw-bg-iron-700", isNew ? styles["upcomingRowNew"] : ""].join(" ")}>
                          <SubscriptionDayDetails date={rows[index]!} count={count_0} />
                        </tr>;
              })}
                </tbody>
              </table>
              {upcomingCounts.length > UPCOMING_PAGE_SIZE && <div ref={upcomingToggleRef} className="tw-pt-3 tw-text-center">
                  {upcomingVisible < upcomingCounts.length ? <ShowMoreButton expanded={false} setExpanded={() => {
              setAnimateFromIndex(upcomingVisible);
              setUpcomingVisible(prev => Math.min(prev + UPCOMING_PAGE_SIZE, upcomingCounts.length));
            }} /> : <ShowMoreButton expanded={true} setExpanded={() => {
              setAnimateFromIndex(null);
              setUpcomingVisible(UPCOMING_PAGE_SIZE);
              requestAnimationFrame(() => {
                upcomingTableTopRef.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "start"
                });
              });
            }} />}
                </div>}
            </> : renderEmptyState(upcomingLoading, "upcoming")}
        </Col>
      </Row>
      <Row className="pt-5">
        <Col className="tw-flex tw-items-center tw-gap-3">
          <span className="font-larger font-bolder decoration-none">
            Past Drops
          </span>
          {redeemedLoading && <CircleLoader size={CircleLoaderSize.MEDIUM} />}
        </Col>
      </Row>
      <Row className="pt-3">
        <Col>
          {redeemedCounts?.length > 0 ? <table className="tw-w-full tw-border-separate tw-border-spacing-0 tw-overflow-hidden tw-rounded-xl tw-border tw-border-iron-700 tw-bg-iron-900">
              <caption className="tw-sr-only">
                Table listing past meme card subscription redemptions
              </caption>
              <thead>
                <tr className="tw-bg-iron-900 tw-text-left tw-text-sm tw-uppercase tw-tracking-wider tw-text-gray-300">
                  <th className="tw-w-3/4 tw-border-b tw-border-iron-700 tw-px-6 tw-py-3 tw-font-semibold">
                    Meme Card
                  </th>
                  <th className="tw-w-1/4 tw-border-b tw-border-iron-700 tw-px-6 tw-py-3 tw-text-center tw-font-semibold">
                    Subscriptions
                  </th>
                </tr>
              </thead>
              <tbody>
                {redeemedCounts.map((count_1, index_0) => <tr key={count_1.token_id} className={index_0 % 2 === 0 ? "tw-bg-iron-800 hover:tw-bg-iron-700" : "tw-bg-iron-900 hover:tw-bg-iron-700"}>
                    <RedeemedSubscriptionDetails count={count_1} />
                  </tr>)}
              </tbody>
            </table> : renderEmptyState(redeemedLoading, "past")}
        </Col>
      </Row>
      {totalRedeemed > PAGE_SIZE && redeemedPage !== null && <div className="tw-py-4 tw-text-center" aria-live="polite" aria-atomic="true">
          <Pagination page={redeemedPage} pageSize={PAGE_SIZE} totalResults={totalRedeemed} setPage={(newPage: number) => {
        setRedeemedPage(newPage);
        pastDropsTarget.current?.scrollIntoView({
          behavior: "smooth"
        });
      }} />
        </div>}
      {shouldShowDownloadSection && <Row className="pt-5">
          <Col>
            <div className="tw-rounded-xl tw-border tw-border-iron-700 tw-bg-iron-900 tw-p-5">
              <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between">
                <h2 className="tw-mb-0 tw-text-base tw-font-semibold tw-text-white">
                  Redeemed Subscriptions Report
                </h2>
                <div className="tw-flex tw-w-full tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center lg:tw-w-auto lg:tw-shrink-0">
                  {availableSeasons.length > 0 && <Form.Group className="tw-mb-0 tw-min-w-[180px]">
                      <Form.Select id="redeemed-meme-subscription-season" aria-label="Redeemed meme subscription counts season" className={`tw-h-9 tw-rounded-lg tw-border-0 tw-bg-iron-800 tw-px-3 tw-text-sm tw-text-iron-50 tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 ${isDownloadingCsv ? "tw-pointer-events-none tw-opacity-50" : ""}`} value={selectedSeasonId} onChange={e => {
                  setSelectedSeasonId(e.currentTarget.value);
                }}>
                        <option value="">All seasons</option>
                        {[...availableSeasons].reverse().map(season_0 => <option key={season_0.id} value={season_0.id.toString()}>
                            {season_0.display}
                          </option>)}
                      </Form.Select>
                    </Form.Group>}
                  <button type="button" onClick={onDownloadCsv} disabled={isDownloadingCsv} className="tw-flex tw-h-9 tw-min-w-[180px] tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-lg tw-border-0 tw-bg-primary-500 tw-px-5 tw-text-sm tw-font-semibold tw-text-white tw-transition tw-duration-300 tw-ease-out hover:tw-bg-primary-600 disabled:tw-cursor-not-allowed disabled:tw-opacity-60">
                    {isDownloadingCsv ? <>
                        <svg className="tw-h-4 tw-w-4 tw-animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                          <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Downloading
                      </> : "Download"}
                  </button>
                </div>
              </div>
            </div>
          </Col>
        </Row>}
    </Container>;
}
function getRedeemedCsvFilename(season: MemeSeason | null): string {
  if (season) {
    return `redeemed-meme-subscription-counts-szn-${season.id}.csv`;
  }
  return "redeemed-meme-subscription-counts-all-seasons.csv";
}
function SubscriptionDayDetails(props) {
  const $ = _c(19);
  let t0;
  if ($[0] !== props.count.token_id) {
    t0 = <span>The Memes #{props.count.token_id}</span>;
    $[0] = props.count.token_id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.date.seasonIndex) {
    t1 = displayedSeasonNumberFromIndex(props.date.seasonIndex);
    $[2] = props.date.seasonIndex;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props.date.utcDay) {
    t2 = formatFullDate(props.date.utcDay);
    $[4] = props.date.utcDay;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <span className="tw-text-sm tw-text-gray-400">SZN {t1}{" / "}{t2}</span>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t0 || $[10] !== t3) {
    t4 = <td className="tw-border-t tw-border-iron-700 tw-px-6 tw-py-4 tw-align-middle tw-text-white"><div className="tw-flex tw-flex-col">{t0}{t3}</div></td>;
    $[9] = t0;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== props.count.count) {
    t5 = props.count.count > 0 ? props.count.count.toLocaleString() : "0";
    $[12] = props.count.count;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t5) {
    t6 = <td className="tw-border-t tw-border-iron-700 tw-px-6 tw-py-4 tw-text-center tw-align-middle tw-text-white">{t5}</td>;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t4 || $[17] !== t6) {
    t7 = <>{t4}{t6}</>;
    $[16] = t4;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}
function RedeemedSubscriptionDetails(props) {
  const $ = _c(47);
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== props.count.image_url || $[1] !== props.count.mint_date || $[2] !== props.count.name || $[3] !== props.count.szn || $[4] !== props.count.token_id) {
    const dateTime = Time.fromString(props.count.mint_date);
    t9 = "tw-border-t tw-border-iron-700 tw-px-6 tw-py-4 tw-align-middle tw-text-white";
    t7 = "tw-flex tw-flex-wrap tw-items-center tw-gap-3";
    const t10 = props.count.name || "Meme card";
    if ($[15] !== props.count.image_url || $[16] !== t10) {
      t8 = <div className="tw-flex tw-h-[50px] tw-w-[50px] tw-shrink-0 tw-items-center tw-justify-center"><Image unoptimized={true} src={props.count.image_url} alt={t10} width={0} height={0} className="tw-rounded-xs tw-h-auto tw-max-h-full tw-w-auto tw-max-w-full" /></div>;
      $[15] = props.count.image_url;
      $[16] = t10;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    t5 = "tw-flex tw-flex-col";
    const t11 = `/the-memes/${props.count.token_id}`;
    const t12 = `View The Memes card #${props.count.token_id} - ${props.count.name}`;
    if ($[18] !== props.count.name || $[19] !== props.count.token_id || $[20] !== t11 || $[21] !== t12) {
      t6 = <Link href={t11} className="decoration-hover-underline tw-text-white" aria-label={t12}>#{props.count.token_id} - {props.count.name}</Link>;
      $[18] = props.count.name;
      $[19] = props.count.token_id;
      $[20] = t11;
      $[21] = t12;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    t0 = "tw-text-sm tw-text-gray-400";
    t1 = "SZN ";
    t2 = props.count.szn;
    t3 = " / ";
    t4 = formatFullDate(dateTime.toDate());
    $[0] = props.count.image_url;
    $[1] = props.count.mint_date;
    $[2] = props.count.name;
    $[3] = props.count.szn;
    $[4] = props.count.token_id;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t0 = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
    t8 = $[13];
    t9 = $[14];
  }
  let t10;
  if ($[23] !== t0 || $[24] !== t1 || $[25] !== t2 || $[26] !== t3 || $[27] !== t4) {
    t10 = <span className={t0}>{t1}{t2}{t3}{t4}</span>;
    $[23] = t0;
    $[24] = t1;
    $[25] = t2;
    $[26] = t3;
    $[27] = t4;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t5 || $[31] !== t6) {
    t11 = <div className={t5}>{t6}{t10}</div>;
    $[29] = t10;
    $[30] = t5;
    $[31] = t6;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t11 || $[34] !== t7 || $[35] !== t8) {
    t12 = <div className={t7}>{t8}{t11}</div>;
    $[33] = t11;
    $[34] = t7;
    $[35] = t8;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t12 || $[38] !== t9) {
    t13 = <td className={t9}>{t12}</td>;
    $[37] = t12;
    $[38] = t9;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== props.count.count) {
    t14 = props.count.count > 0 ? props.count.count.toLocaleString() : "0";
    $[40] = props.count.count;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t14) {
    t15 = <td className="tw-border-t tw-border-iron-700 tw-px-6 tw-py-4 tw-text-center tw-align-middle tw-text-white">{t14}</td>;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== t13 || $[45] !== t15) {
    t16 = <>{t13}{t15}</>;
    $[44] = t13;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  return t16;
}